// Define the application's config state to get PARTNER_TOKEN (assuming it's in some global config or context)
import { userSession } from '../../state/session';
import { ApiError } from 'utils/errors';
import { EErrorCode } from 'config/errors-codes';
import { user } from 'utils/auth/user';
import { showToast } from 'state/toast';
import { errorCollection } from 'config/ui/error-collection';

export type TMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

// Define the type for request options
export type RequestOptions = Omit<RequestInit, 'headers' | 'body'> & {
  headers?: Record<string, string>;
  method?: TMethod;
  body?: unknown;
  origin?: string;
  idToken?: boolean;
  suppressToast?: boolean; // Caller owns error reporting — skip both the global error toast and the wrapper's console.error
  // Surface a SESSION_STALE_WRITE 409 as a typed throw instead of a silent undefined, so a
  // compare-and-set caller can rebase on the server state and retry (AVS-4330).
  throwOnStaleWrite?: boolean;
};

// Reusable function to determine the API base URL
export const getApiBaseUrl = (): string => {
  if (process.env.API_URL) {
    return process.env.API_URL;
  }
  throw new Error('API_URL is not defined.');
};

// Map to track active requests by their unique identifier (method + URL)
const activeRequests = new Map<string, AbortController>();

// Helper function to create a unique request identifier
const getRequestId = (method: string, url: string): string => {
  return `${method}:${url}`;
};

// Function to cancel a specific request if it exists
const cancelRequest = (requestId: string, reason: string = 'Duplicate request'): boolean => {
  const controller = activeRequests.get(requestId);
  if (controller) {
    controller.abort(reason);
    activeRequests.delete(requestId);
    return true;
  }
  return false;
};

/**
 * Global error toast interceptor
 * Automatically displays toast notifications for API errors with translated messages
 * @param error - The ApiError to display
 */
function triggerErrorToast(error: ApiError): void {
  const errorDetails = errorCollection[error.code] ?? errorCollection[EErrorCode.INTERNAL_ERROR];

  showToast({
    title: errorDetails.title(),
    message: errorDetails.message(),
    type: 'error',
    notificationId: errorDetails.notificationId,
  });
}

// Reusable function for making API calls with error handling and header setup
export const sendApiRequest = async <T>(
  endpoint: string,
  options: RequestOptions = {},
  auth = true,
  contextToken?: string
): Promise<T | undefined> => {
  const PARTNER_TOKEN = String(userSession().get('partnerToken'));
  const AUTH_TOKEN = user.getAccessToken();
  const ID_TOKEN = user.getIdToken();

  const apiUrl = options.origin ?? getApiBaseUrl();
  const url = `${apiUrl}${endpoint}`;
  const method = options.method || 'GET';

  // Create a unique identifier for this request
  const requestId = getRequestId(method, url);

  // Check if there's already an identical request in progress
  if (activeRequests.has(requestId)) {
    if (import.meta?.env?.MODE === 'development') {
      console.log(`Cancelling previous ${method} request to ${url}`);
    }
    // Cancel the previous request to the same endpoint
    cancelRequest(requestId);
  }

  // Setup headers with PARTNER_TOKEN
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Partner-Token': PARTNER_TOKEN,
    ...(options.headers || {}), // Include any additional headers passed in options
    ...(auth && AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
    ...(auth && options.idToken && ID_TOKEN ? { 'X-Id-Token': ID_TOKEN } : {}),
    ...(contextToken ? { 'Context-Token': `Bearer ${contextToken}` } : {}),
  };

  // Create an AbortController for this request
  const controller = new AbortController();
  const signal = controller.signal;

  // Register this request
  activeRequests.set(requestId, controller);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      method,
      body: options.body ? JSON.stringify(options.body) : undefined, // Serialize the body if present
      signal, // Attach the signal for aborting requests
    });

    if (!response.ok) {
      // Error handling: Try to extract a JSON error message from the response
      const errorData = await response.json().catch(() => null);

      // Stale write: a typed throw for CAS callers (they rebase + retry), a silent discard for
      // everyone else (the WebSocket broadcast delivers current state).
      if (response.status === 409 && errorData?.code === EErrorCode.SESSION_STALE_WRITE) {
        if (options.throwOnStaleWrite) {
          throw new ApiError(EErrorCode.SESSION_STALE_WRITE, 'Stale write rejected', {
            statusCode: 409,
            silent: true,
          });
        }
        return undefined;
      }

      const errorMessage = errorData?.error || response.statusText;
      const errorCode: EErrorCode = errorData?.code || EErrorCode.INTERNAL_ERROR;
      throw new ApiError(errorCode, `Error: ${response.status} - ${errorMessage}`, {
        statusCode: response.status,
        silent: options.suppressToast,
      });
    }

    // Parse the JSON response
    const data = await response.json();
    return data as T;
  } catch (error) {
    if (error === 'Duplicate request') {
      console.warn(`Duplicate request aborted: ${method} ${url}`, error);
    } else {
      // A stale-write signal (only minted above, under throwOnStaleWrite) is expected control
      // flow for the CAS caller — no console noise, no toast, just the typed rethrow.
      const isStaleWriteSignal = error instanceof ApiError && error.code === EErrorCode.SESSION_STALE_WRITE;

      // suppressToast signals the caller owns error reporting — stay silent in the wrapper.
      if (!options.suppressToast && !isStaleWriteSignal) {
        console.error('API request failed:', error);
      }

      if (error instanceof ApiError) {
        if (!options.suppressToast && !isStaleWriteSignal) {
          triggerErrorToast(error);
        }
        throw error;
      }

      throw new ApiError(EErrorCode.INTERNAL_ERROR, (error as Error).message, {
        silent: options.suppressToast,
      });
    }
  } finally {
    // Cleanup: Remove this request from tracking when done
    activeRequests.delete(requestId);
  }
};
